import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../MainLayout';
import { useSession } from '../../contexts/SessionContext';
import { supabase } from '../../lib/supabase_client';
import { MSICCode, parseMSICXml, resetMSICCache } from '../../services/msicService';
import '../../styles/page-base.css';

interface MsicRow {
  code: string;
  description: string;
  malay_description: string | null;
  created_at: string | null;
}

interface MsicSyncLog {
  synced_at: string;
  synced_by_email: string | null;
  file_name: string | null;
  upserted: number;
  removed: number;
}

const formatSyncDate = (iso: string) => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

export default function AdminMsic() {
  const navigate = useNavigate();
  const { isAdmin, loading: sessionLoading } = useSession();
  const [rows, setRows] = useState<MsicRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [parsedItems, setParsedItems] = useState<MSICCode[] | null>(null);
  const [lastSync, setLastSync] = useState<MsicSyncLog | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchPreview = useCallback(async () => {
    try {
      setLoading(true);
      const [{ count, error: countError }, { data, error }, { data: syncData, error: syncError }] = await Promise.all([
        supabase.from('msic_codes').select('id', { count: 'exact', head: true }),
        supabase
          .from('msic_codes')
          .select('code, description, malay_description, created_at')
          .order('code')
          .limit(20),
        supabase
          .from('msic_sync_log')
          .select('synced_at, synced_by_email, file_name, upserted, removed')
          .order('synced_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (countError) throw countError;
      if (error) throw error;
      if (syncError) throw syncError;

      setTotalCount(count || 0);
      setRows((data || []) as MsicRow[]);
      setLastSync((syncData || null) as MsicSyncLog | null);
    } catch (err) {
      console.error('Error fetching MSIC codes:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load MSIC codes');
      setRows([]);
      setTotalCount(0);
      setLastSync(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!sessionLoading && isAdmin) {
      fetchPreview();
    }
  }, [sessionLoading, isAdmin, fetchPreview]);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setStatusMessage(null);
    setErrorMessage(null);
    setParsedItems(null);
    setSelectedFileName(null);

    if (!file) return;

    try {
      if (!file.name.toLowerCase().endsWith('.xml')) {
        throw new Error('Please upload an XML file.');
      }

      const xml = await file.text();
      const parsed = parseMSICXml(xml);
      setSelectedFileName(file.name);
      setParsedItems(parsed);
      setStatusMessage(`Ready to sync ${parsed.length} codes from ${file.name}.`);
    } catch (err) {
      console.error('MSIC XML parse failed:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to read the XML file');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSync = async () => {
    if (!parsedItems || parsedItems.length === 0) {
      setErrorMessage('Please upload an MSIC XML file first.');
      return;
    }

    setSyncing(true);
    setStatusMessage(null);
    setErrorMessage(null);

    try {
      const items = parsedItems.map((item) => ({
        code: item.code,
        description: item.description,
        malay_description: item.malayDescription,
      }));

      const { data, error } = await supabase.functions.invoke('import-msic', {
        body: { items, file_name: selectedFileName },
      });

      if (error) {
        throw new Error(error.message || 'Failed to sync MSIC codes');
      }
      if (data?.error) {
        throw new Error(data.details ? `${data.error}: ${data.details}` : data.error);
      }

      resetMSICCache();
      setParsedItems(null);
      setSelectedFileName(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setStatusMessage(
        `Synced ${data?.upserted ?? items.length} MSIC codes` +
        (data?.removed ? `, removed ${data.removed} stale codes` : '') +
        '.'
      );
      await fetchPreview();
    } catch (err) {
      console.error('MSIC sync failed:', err);
      setErrorMessage(err instanceof Error ? err.message : 'Failed to sync MSIC codes');
    } finally {
      setSyncing(false);
    }
  };

  const filtered = rows.filter((row) => {
    const q = searchTerm.toLowerCase();
    return (
      row.code.toLowerCase().includes(q) ||
      row.description.toLowerCase().includes(q) ||
      (row.malay_description || '').toLowerCase().includes(q)
    );
  });

  if (!sessionLoading && !isAdmin) {
    return (
      <MainLayout>
        <div className="page-container">
          <div className="max-w-3xl mx-auto py-12 text-center text-gray-600">
            You do not have permission to view this page.
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="page-container">
        <div className="max-w-6xl mx-auto space-y-6">
          <div className="page-header">
            <div className="page-header-left">
              <div className="text-sm text-gray-500 mb-1">Admin Only</div>
              <h1 className="page-title">MSIC Codes</h1>
            </div>
            <div className="page-header-right flex gap-2">
              <button
                type="button"
                onClick={() => navigate('/admin/customers')}
                className="f-btn f-btn-primary"
              >
                <span>Back to Customers</span>
              </button>
              <button
                type="button"
                onClick={() => fetchPreview()}
                className="f-btn f-btn-success"
                disabled={loading || syncing}
              >
                Refresh
              </button>
              <button
                type="button"
                onClick={handleSync}
                className="f-btn f-btn-primary"
                disabled={syncing || !parsedItems}
              >
                {syncing ? 'Syncing...' : 'Sync uploaded XML'}
              </button>
            </div>
          </div>

          <div className="page-card space-y-4">
            <p className="text-sm text-gray-600">
              Filing MSIC pickers and backend lookups use this table. Upload an MSIC XML file,
              then sync to replace the current list.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xml,text/xml,application/xml"
                onChange={handleFileChange}
                disabled={syncing}
                className="block text-sm text-gray-700 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              />
              {selectedFileName && (
                <span className="text-sm text-gray-600">
                  {selectedFileName}: <strong>{parsedItems?.length ?? 0}</strong> codes ready
                </span>
              )}
            </div>
            <div className="text-sm text-gray-700 space-y-1">
              <div>
                <strong>{totalCount}</strong> codes in database
              </div>
              <div>
                Last sync:{' '}
                {lastSync ? (
                  <>
                    <strong>{formatSyncDate(lastSync.synced_at)}</strong>
                    {lastSync.file_name ? ` from ${lastSync.file_name}` : ''}
                    {lastSync.synced_by_email ? ` by ${lastSync.synced_by_email}` : ''}
                    {` (${lastSync.upserted} upserted`}
                    {lastSync.removed ? `, ${lastSync.removed} removed` : ''}
                    {')'}
                  </>
                ) : (
                  <span className="text-gray-500">No sync recorded yet</span>
                )}
              </div>
            </div>
            {statusMessage && (
              <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                {statusMessage}
              </div>
            )}
            {errorMessage && (
              <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {errorMessage}
              </div>
            )}

            <div className="flex items-center justify-between">
              <input
                type="text"
                placeholder="Filter preview by code or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
              <div className="text-sm text-gray-500 ml-4">Preview of first 20 codes</div>
            </div>

            <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
              {loading ? (
                <div className="text-center py-10 text-sm text-gray-600">Loading MSIC codes...</div>
              ) : (
                <table className="page-table">
                  <thead>
                    <tr>
                      <th>Code</th>
                      <th>Description</th>
                      <th>Malay Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan={3} className="text-center text-gray-500">
                          No MSIC codes to preview.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((row) => (
                        <tr key={row.code}>
                          <td>{row.code}</td>
                          <td>{row.description}</td>
                          <td>{row.malay_description || '-'}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
